import React from "react";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import useScrollToHomeSection from "../../hooks/useScrollToHomeSection.js";
import {
  trackCTA,
  trackSectionViewed,
  useSectionOnceObserver,
} from "../../lib/analytics";
import { WSE_SECTION } from "./sections.js";
import useLocalizedHref from "../../hooks/useLocalizedHref.js";

/**
 * Closing statement.
 *
 * Two actions, in the order a visitor from search actually wants them: the plan
 * that contains seating, and — for whoever is not ready to look at a price —
 * the RSVP, which is where the names on a seating board come from and the one
 * page that answers "what do I have to do before any of this is useful?".
 *
 * `trackLead` fires on neither. A scroll to the pricing section is a CTA click
 * and nothing more, and an internal navigation to a sibling page is not a lead
 * by any definition this site uses. The one link on this page that IS counted
 * as a lead is `/try`, and it deliberately stays in the hero and the demo block
 * rather than closing the page: the last thing a visitor sees should be the
 * plan, not a disposable example.
 */
const SeatingFinalCta = () => {
  const { t } = useTranslation();
  const localize = useLocalizedHref();
  const analyticsContext = useAnalyticsContext();
  const scrollToPlans = useScrollToHomeSection("pricing");

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WSE_SECTION.FINAL_CTA, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section wps-section wps-final">
      <div className="container">
        <div className="wps-final-inner">
          <h2 className="heading-136">
            {t("weddingSeating.finalCta.heading")}
          </h2>
          <p className="body-20 wps-final-body">
            {t("weddingSeating.finalCta.body")}
          </p>
          <Button
            href={localize("/#pricing")}
            text={t("weddingSeating.finalCta.ctaPrimary")}
            className="button-lg"
            onClick={(e) => {
              trackCTA("wedding_seating_final_cta", {
                ...analyticsContext,
                ctaName: "see_plans",
              });
              scrollToPlans(e);
            }}
          />
          <p className="dwi-final-secondary">
            <a
              className="wps-text-link"
              href={localize("/wedding-rsvp")}
              onClick={() =>
                trackCTA("wedding_seating_final_cta", {
                  ...analyticsContext,
                  ctaName: "wedding_rsvp",
                })
              }
            >
              {t("weddingSeating.finalCta.ctaSecondary")}
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default SeatingFinalCta;
